import { Injectable, NgZone } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AnalyticsService } from './analytics.service';

export type LanguageSheetMode = 'first' | 'switch';

interface LanguageSheetState {
  isOpen: boolean;
  mode: LanguageSheetMode;
}

/**
 * Drives the global language-selection bottom sheet — a clone of `AttractionDetailsSheetService`,
 * keyed on the open mode ('first' = first-visit suggestion, 'switch' = reopened from the header
 * flag chip) instead of an entity id.
 *
 * Hosted once in AppComponent above the router-outlet, so it overlays the (loading) menu that
 * opened it. Opening pushes a synthetic same-URL history entry so browser Back closes the sheet;
 * a programmatic navigation tears it down so the overlay + `body.lang-sheet-open` scroll-lock
 * never persist over the next screen.
 */
@Injectable({ providedIn: 'root' })
export class LanguageSheetService {
  private state = new BehaviorSubject<LanguageSheetState>({ isOpen: false, mode: 'first' });

  /** Emits on every open/close; AppComponent binds this with the async pipe. */
  readonly changes$: Observable<LanguageSheetState> = this.state.asObservable();

  /** True while our synthetic history entry is on the stack (so Back closes the sheet). */
  private hasHistoryEntry = false;

  constructor(private router: Router, private ngZone: NgZone, private analytics: AnalyticsService) {
    // Browser Back: the browser has already popped our synthetic entry, so just close in place.
    window.addEventListener('popstate', this.onPopState);

    // Any programmatic route change while the sheet is open must close it; the router is taking
    // over history, so we must not pop our own entry here.
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe(() => {
        if (this.isOpen) {
          this.hasHistoryEntry = false;
          this.reset();
        }
      });
  }

  get isOpen(): boolean {
    return this.state.value.isOpen;
  }

  get mode(): LanguageSheetMode {
    return this.state.value.mode;
  }

  /** Open the sheet over the current screen in the given mode. */
  open(mode: LanguageSheetMode): void {
    // Logged here rather than at each trigger: a diner reaching for the header chip means something
    // quite different from a first-visit offer the app pushed at them, and `mode` already says which.
    this.analytics.log('language_sheet_open', { entry_point: mode });
    document.body.classList.add('lang-sheet-open');
    // Push a same-URL history entry once per open session so Back closes the sheet.
    if (!this.hasHistoryEntry) {
      history.pushState({ langSheet: true }, '', location.href);
      this.hasHistoryEntry = true;
    }
    this.state.next({ isOpen: true, mode });
  }

  /** User-initiated close (X / backdrop / row tap). Pops the synthetic history entry from open. */
  close(): void {
    const hadEntry = this.hasHistoryEntry;
    this.reset();
    if (hadEntry) {
      history.back();
    }
  }

  private onPopState = (): void => {
    if (!this.isOpen) return;
    this.ngZone.run(() => {
      this.hasHistoryEntry = false;
      this.reset();
    });
  };

  /** Clear sheet state (and body scroll-lock) without touching history. */
  private reset(): void {
    this.hasHistoryEntry = false;
    document.body.classList.remove('lang-sheet-open');
    this.state.next({ isOpen: false, mode: this.state.value.mode });
  }
}
